import { Box, Chip, Typography } from "@mui/material";
import { useResponsiveValue } from "Hooks/useResponsiveValue";
import { type LicenseShareDto, SettlementStatus } from "Infrastructure/Api/Api";
import { formatPhone } from "Utils/PhoneUtils";
import { PAYMENT_STATUS_TO_INFO } from "Utils/SettlementUtils";
import { appUrl } from "Utils/UrlUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import {
  type MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { type ReactNode, useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";

const COMPONENT_RESOURCES = Resources.Hunter.Merchants.ListPage.Table;

type Props = {
  isLoading?: boolean;
  data?: Array<LicenseShareDto>;
  enableLicenseGrouping: boolean;
  title?: ReactNode;
  density?: "compact" | "comfortable";
};

export const SharedMerchantsTable = ({
  isLoading,
  data = [],
  enableLicenseGrouping,
  title,
  density,
}: Props) => {
  const { t } = useResource();
  const getResponsiveValue = useResponsiveValue();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();

  const columns = useMemo<Array<MRT_ColumnDef<LicenseShareDto>>>(() => {
    const shareLicenseColumn: MRT_ColumnDef<LicenseShareDto> = {
      accessorKey: "licenseNumber",
      header: t(COMPONENT_RESOURCES.Columns.LicenseNumber),
      size: 110,
      enableGrouping: false,
      GroupedCell: ({ cell, row }) => {
        const licenseNumber = cell.getValue<string>();
        const shareCount = row.subRows?.length || 0;
        return (
          <Typography
            fontSize={12}
            fontWeight={400}
            noWrap
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <Box component="span" fontWeight={700}>
              No. {licenseNumber || "-"}
            </Box>{" "}
            <Box component="span" fontWeight={400}>
              ({shareCount})
            </Box>
          </Typography>
        );
      },
      Cell: ({ row }) => {
        const shareLicenseNo = row.original.licenseNumber;
        return (
          <Typography fontSize={12} fontWeight={400} noWrap>
            {shareLicenseNo || "-"}
          </Typography>
        );
      },
    };

    return [
      ...(enableLicenseGrouping ? [shareLicenseColumn] : []),
      {
        accessorKey: "companyName",
        header: t(COMPONENT_RESOURCES.Columns.Party.CompanyName),
        size: getResponsiveValue([150, 200]),
        enableGrouping: false,
        Cell: ({ cell }) => (
          <Typography fontSize={12} fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        // String accessor keeps the PhoneNumber column sortable and searchable.
        accessorFn: ({ phone }) => formatPhone(phone),
        id: "phone",
        header: t(COMPONENT_RESOURCES.Columns.Party.Phone),
        size: getResponsiveValue([120, 140]),
        enableGrouping: false,
      },
      {
        accessorKey: "email",
        header: t(COMPONENT_RESOURCES.Columns.Party.Email),
        enableGrouping: false,
        size: 250,
      },
      {
        accessorKey: "sharePercentage",
        header: t(COMPONENT_RESOURCES.Columns.SharePercentage),
        enableGrouping: false,
        size: getResponsiveValue([100, 120]),
        Cell: ({ cell }) => {
          const value = cell.getValue<number>();
          return (
            <Typography fontSize={12} fontWeight={400}>
              {value != null ? `${value.toFixed(2)}%` : "-"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "transactionsCount",
        header: t(COMPONENT_RESOURCES.Columns.TransactionsCount),
        size: 170,
        enableGrouping: false,
      },
      {
        accessorKey: "settlementStatus",
        header: t(COMPONENT_RESOURCES.Columns.Party.SettlementStatus),
        size: 160,
        enableGrouping: false,
        Cell: ({ row }) => {
          const settlementStatus = row.original.settlementStatus;
          if (!settlementStatus) {
            return <span>-</span>;
          }
          const { label, backgroundColor } =
            PAYMENT_STATUS_TO_INFO[settlementStatus];
          return (
            <Chip
              label={label}
              size="small"
              sx={{ backgroundColor, color: "white", fontWeight: 600 }}
            />
          );
        },
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: t(
              Resources.Components.Settlements.SettlementStatus[
                SettlementStatus.Paid
              ],
            ),
            value: SettlementStatus.Paid,
          },
          {
            label: t(
              Resources.Components.Settlements.SettlementStatus[
                SettlementStatus.Unpaid
              ],
            ),
            value: SettlementStatus.Unpaid,
          },
          {
            label: t(
              Resources.Components.Settlements.SettlementStatus[
                SettlementStatus.Overdue
              ],
            ),
            value: SettlementStatus.Overdue,
          },
        ],
        filterFn: (row, _id, filterValue) => {
          return row.original.settlementStatus === filterValue;
        },
      },
    ];
  }, [getResponsiveValue, t, enableLicenseGrouping]);

  const table = useMaterialReactTable({
    data,
    columns,
    localization,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableGrouping: enableLicenseGrouping,
    renderToolbarInternalActions: enableLicenseGrouping
      ? () => null
      : undefined,
    muiToolbarAlertBannerProps: enableLicenseGrouping
      ? { sx: { display: "none" } }
      : undefined,
    initialState: {
      pagination: { pageSize: 15, pageIndex: 0 },
      density: density || getResponsiveValue(["compact", "comfortable"]),
      ...(enableLicenseGrouping && {
        grouping: ["licenseNumber"],
        expanded: true,
      }),
    },
    state: {
      isLoading,
    },
    muiTableBodyRowProps: ({ row }) => {
      // Don't make grouped rows clickable
      if (row.getIsGrouped()) {
        return {};
      }
      const url = `${appUrl("shared-merchant-detail")}/${row.original.merchantPublicID}`;
      const onClick = createRowClickHandler(url);
      return {
        onClick,
        sx: {
          cursor: "pointer",
        },
      };
    },
    muiTopToolbarProps: {
      sx: {
        "& > div": {
          p: 0,
        },
      },
    },
    renderTopToolbarCustomActions: () =>
      title ? (
        <Box ml={3} display="flex" alignSelf="center">
          <Typography fontWeight={500} lineHeight={1.4} fontSize={[14, 16, 18]}>
            {title}
          </Typography>
        </Box>
      ) : null,
    muiTablePaperProps: {
      sx: {
        borderRadius: "10px",
      },
    },
  });

  return <MaterialReactTable table={table} />;
};
